import { createFileRoute } from '@tanstack/react-router';
import { usePublicHsPack, useRequestHsInfoMutation } from 'api/hs';
import { COMPANY } from 'features/Landing/content';
import { Container, PhoneLink } from 'features/Landing/primitives';
import { useState, type FC } from 'react';

const formatDate = (value: string | Date) => new Date(value).toLocaleDateString('en-NZ');

const riskBadge = (likelihood: number, severity: number) => {
  const score = likelihood * severity;
  const tone =
    score >= 15
      ? 'bg-red-100 text-red-800'
      : score >= 8
        ? 'bg-amber-100 text-amber-800'
        : 'bg-green-100 text-green-800';
  return <span className={`rounded px-2 py-0.5 font-mono text-xs ${tone}`}>{score}</span>;
};

const SectionCard: FC<{ sheet: string; title: string; children: React.ReactNode }> = ({
  sheet,
  title,
  children
}) => (
  <section className="rounded-lg border border-ink-200 bg-white p-6 sm:p-8">
    <p className="font-mono text-[11px] tracking-[0.2em] text-ink-500 uppercase">{sheet}</p>
    <h2 className="font-display mt-1 mb-5 text-xl font-bold text-ink-900">{title}</h2>
    {children}
  </section>
);

const InfoRequestForm: FC<{ token: string }> = ({ token }) => {
  const request = useRequestHsInfoMutation();
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [sent, setSent] = useState(false);

  if (sent) {
    return (
      <p className="rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
        Request sent. We&rsquo;ll come back to you shortly.
      </p>
    );
  }

  const inputClasses =
    'w-full rounded-md border border-ink-300 bg-white px-4 py-3 text-sm text-ink-900 focus:border-brand-600 focus:outline-2 focus:outline-offset-0 focus:outline-brand-500/30';

  return (
    <form
      className="space-y-4"
      onSubmit={async (event) => {
        event.preventDefault();
        await request.mutateAsync({ token, ...form });
        setSent(true);
      }}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <input
          required
          placeholder="Your name"
          className={inputClasses}
          value={form.name}
          onChange={(event) => setForm({ ...form, name: event.target.value })}
        />
        <input
          required
          type="email"
          placeholder="Your email"
          className={inputClasses}
          value={form.email}
          onChange={(event) => setForm({ ...form, email: event.target.value })}
        />
      </div>
      <textarea
        required
        minLength={5}
        rows={3}
        placeholder="What else do you need? (e.g. insurance certificate, SWMS for a specific task)"
        className={`${inputClasses} resize-y`}
        value={form.message}
        onChange={(event) => setForm({ ...form, message: event.target.value })}
      />
      <button
        type="submit"
        disabled={request.isPending}
        className="rounded-md bg-brand-600 px-6 py-3 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
      >
        Request information
      </button>
    </form>
  );
};

const HsPortalContent: FC<{ token: string }> = ({ token }) => {
  const { data: pack, isLoading, isError } = usePublicHsPack(token);

  if (isLoading) {
    return <p className="py-24 text-center font-mono text-sm text-ink-500">Loading compliance pack…</p>;
  }

  if (isError || !pack) {
    return (
      <div className="py-24 text-center">
        <h1 className="font-display text-2xl font-bold text-ink-900">Link not found or expired</h1>
        <p className="mt-3 text-ink-600">
          Ask your contact at {COMPANY.name} for a fresh link, or call{' '}
          <PhoneLink className="font-semibold text-brand-600" />.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 py-12">
      <header className="rounded-lg border border-ink-200 bg-white p-6 sm:p-8">
        <p className="font-mono text-xs tracking-[0.25em] text-brand-600 uppercase">
          Health &amp; safety compliance pack
        </p>
        <h1 className="font-display mt-2 text-3xl font-bold text-ink-900">{pack.label}</h1>
        <p className="mt-2 text-sm text-ink-600">
          Prepared by {COMPANY.legalName} · generated {formatDate(pack.generatedAt)} · read-only
        </p>
      </header>

      <SectionCard sheet="HS-01" title={`Hazard register (${pack.hazards.length} active)`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-200 text-left font-mono text-[11px] tracking-[0.15em] text-ink-500 uppercase">
                <th className="py-2 pr-4 font-medium">Hazard</th>
                <th className="py-2 pr-4 font-medium">Category</th>
                <th className="py-2 pr-4 font-medium">Risk</th>
                <th className="py-2 font-medium">Controls</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {pack.hazards.map((hazard, index) => (
                <tr key={index}>
                  <td className="py-2.5 pr-4 font-medium text-ink-900">{hazard.title}</td>
                  <td className="py-2.5 pr-4 text-ink-600">{hazard.category || '-'}</td>
                  <td className="py-2.5 pr-4">{riskBadge(hazard.likelihood, hazard.severity)}</td>
                  <td className="py-2.5 text-ink-600">{hazard.controls || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard sheet="HS-02" title="Toolbox meetings (latest 20)">
        <ul className="divide-y divide-ink-100">
          {pack.meetings.map((meeting, index) => (
            <li key={index} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm">
              <span className="text-ink-900">
                <span className="font-mono text-xs text-ink-500">{formatDate(meeting.date)}</span>
                {' - '}
                {meeting.topics}
              </span>
              <span className="font-mono text-xs text-ink-500">
                {meeting.signedCount}/{meeting.attendeeCount} signed
              </span>
            </li>
          ))}
          {!pack.meetings.length && <li className="py-2 text-sm text-ink-500">None recorded yet.</li>}
        </ul>
      </SectionCard>

      <div className="grid gap-6 md:grid-cols-2">
        <SectionCard sheet="HS-03" title="Site inductions">
          <ul className="divide-y divide-ink-100">
            {pack.inductions.map((induction, index) => (
              <li key={index} className="flex items-center justify-between py-2 text-sm">
                <span className="text-ink-900">{induction.name}</span>
                <span className="font-mono text-xs text-ink-500">{formatDate(induction.inductedAt)}</span>
              </li>
            ))}
            {!pack.inductions.length && <li className="py-2 text-sm text-ink-500">None recorded yet.</li>}
          </ul>
        </SectionCard>

        <SectionCard sheet="HS-04" title="Documents held">
          <ul className="divide-y divide-ink-100">
            {pack.documents.map((doc, index) => (
              <li key={index} className="flex items-center justify-between py-2 text-sm">
                <span className="text-ink-900">{doc.name}</span>
                <span className="font-mono text-xs text-ink-500">
                  {doc.category}
                  {doc.expiresAt ? ` · expires ${formatDate(doc.expiresAt)}` : ''}
                </span>
              </li>
            ))}
            {!pack.documents.length && <li className="py-2 text-sm text-ink-500">Document list on request.</li>}
          </ul>
        </SectionCard>
      </div>

      {pack.substances.length > 0 && (
        <SectionCard
          sheet="HS-05"
          title={`Hazardous substances (${pack.substances.length})`}
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-ink-100 text-xs uppercase tracking-wide text-ink-500">
                  <th className="py-2 pr-4 font-medium">Product</th>
                  <th className="py-2 pr-4 font-medium">UN</th>
                  <th className="py-2 pr-4 font-medium">Max qty</th>
                  <th className="py-2 pr-4 font-medium">Hazard classes</th>
                  <th className="py-2 font-medium">Storage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {pack.substances.map((substance, index) => (
                  <tr key={index} className="align-top">
                    <td className="py-2 pr-4 text-ink-900">{substance.productName}</td>
                    <td className="py-2 pr-4 font-mono text-xs text-ink-500">
                      {substance.unNumber || '-'}
                    </td>
                    <td className="py-2 pr-4 text-ink-700">{substance.maxQuantity || '-'}</td>
                    <td className="py-2 pr-4 font-mono text-xs text-ink-500">
                      {substance.hazardClasses || '-'}
                    </td>
                    <td className="py-2 text-xs text-ink-500">{substance.storageRequirements}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}

      <SectionCard sheet="HS-06" title="Need something else?">
        <InfoRequestForm token={token} />
      </SectionCard>
    </div>
  );
};

const HsPortalPage: FC = () => {
  // TanStack's file-route pattern is circular by design: Route is read at render
  // time, long after module init.
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const { token } = Route.useParams();
  return (
    <div className="min-h-[70vh] bg-[#f2f3f1] pt-24">
      <Container className="max-w-4xl">
        <HsPortalContent token={token} />
      </Container>
    </div>
  );
};

export const Route = createFileRoute('/_landing/hs/$token')({
  ssr: false,
  head: () => ({
    meta: [
      { title: 'Health & Safety compliance pack | Dreams Built' },
      { name: 'robots', content: 'noindex' }
    ]
  }),
  component: HsPortalPage
});
